import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface Conversation {
  id: string;
  channel: 'web' | 'phone';
  status: string;
  contactId: string | null;
  contactPhone: string | null;
  roomName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export default function ConversationsPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<{
    channel?: 'web' | 'phone';
    status?: string;
  }>({});

  const pageSize = 20;

  useEffect(() => {
    loadConversations();
  }, [page, filter]);

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (filter.channel) params.append('channel', filter.channel);
      if (filter.status) params.append('status', filter.status);

      const response = await api.get<{
        items: Conversation[];
        total: number;
      }>(`/conversations?${params}`);

      setConversations(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Conversations</h1>
          <p className="mt-1 text-surface-400">
            View and manage all voice conversations
          </p>
        </div>
        <Link to="/test-call" className="btn-primary">
          New Test Call
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-body flex items-center gap-4">
          <div>
            <label className="block text-xs font-medium text-surface-500 mb-1">Channel</label>
            <select
              value={filter.channel || ''}
              onChange={(e) => setFilter({ ...filter, channel: e.target.value as 'web' | 'phone' | undefined || undefined })}
              className="input py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="web">Web</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-500 mb-1">Status</label>
            <select
              value={filter.status || ''}
              onChange={(e) => setFilter({ ...filter, status: e.target.value || undefined })}
              className="input py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="connecting">Connecting</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex-1" />
          <div className="text-sm text-surface-400">
            {total} conversation{total !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-800">
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-surface-500">
                    Loading...
                  </td>
                </tr>
              ) : conversations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-surface-500">
                    No conversations found
                  </td>
                </tr>
              ) : (
                conversations.map((conv) => (
                  <tr key={conv.id} className="hover:bg-surface-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/conversations/${conv.id}`}
                        className="font-mono text-sm text-primary-400 hover:text-primary-300"
                      >
                        {conv.id.slice(0, 16)}...
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${conv.channel === 'web' ? 'badge-info' : 'badge-warning'}`}>
                        {conv.channel}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={conv.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-400">
                      {conv.contactPhone ? (
                        <span className="font-mono">{conv.contactPhone}</span>
                      ) : conv.contactId ? (
                        <span className="font-mono text-xs">{conv.contactId.slice(0, 12)}...</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-400">
                      {conv.startedAt
                        ? new Date(conv.startedAt).toLocaleString()
                        : new Date(conv.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-400">
                      {conv.startedAt && conv.endedAt
                        ? formatDuration(
                            new Date(conv.endedAt).getTime() - new Date(conv.startedAt).getTime()
                          )
                        : conv.status === 'active'
                        ? 'In progress'
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/conversations/${conv.id}`}
                        className="text-sm text-surface-400 hover:text-surface-200"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-surface-800 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-surface-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="btn-secondary py-1.5 px-3 text-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="btn-secondary py-1.5 px-3 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusClasses: Record<string, string> = {
    active: 'badge-success',
    completed: 'badge-info',
    pending: 'badge-warning',
    connecting: 'badge-warning',
    failed: 'badge-error',
    cancelled: 'badge-error',
  };

  return <span className={`badge ${statusClasses[status] || 'badge-info'}`}>{status}</span>;
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}




